import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { useTheme } from '../context/ThemeContext';
import AddressSelector from './AddressSelector';
import axios from 'axios';
import { API_URL } from '../config/api';
import './Navbar.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, updateUser, loading: authLoading } = useAuth();
  const { cart, clearCart } = useCart();
  const { setCurrentAddress } = useDeliveryAddress();
  const { preference: themePreference, setPreference: setThemePreference } = useTheme();
  
  // Calculate cart item count to trigger re-renders when cart changes
  const cartItemCount = useMemo(() => {
    return cart.reduce((total, restaurantCart) => {
      return total + restaurantCart.items.reduce((sum, item) => sum + item.quantity, 0);
    }, 0);
  }, [cart]);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLang = localStorage.getItem('i18nextLng') || i18n.language || 'en';
    return savedLang === 'fi' ? 'fi' : 'en';
  });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasStores, setHasStores] = useState(true); // show Stores link until we know there are none

  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang && (savedLang === 'fi' || savedLang === 'en')) {
      setCurrentLanguage(savedLang);
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  useEffect(() => {
    if (!authLoading && user?.settings?.language) {
      const userLang = user.settings.language;
      setCurrentLanguage(userLang);
      i18n.changeLanguage(userLang);
    }
  }, [user, i18n, authLoading]);

  // Handle navbar visibility on scroll
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Always show navbar at the top (within 10px)
          if (currentScrollY < 10) {
            setIsNavbarVisible(true);
          } else {
            // Show navbar when scrolling up, hide when scrolling down
            if (currentScrollY < lastScrollY) {
              // Scrolling up - show navbar (even slightly)
              setIsNavbarVisible(true);
            } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
              // Scrolling down and past 100px - hide navbar
              setIsNavbarVisible(false);
            }
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.navbar-mobile-menu')) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/stores/count`)
      .then((res) => {
        if (!cancelled && res.data && typeof res.data.count === 'number') {
          setHasStores(res.data.count > 0);
        }
      })
      .catch(() => {
        if (!cancelled) setHasStores(false);
      });
    return () => { cancelled = true; };
  }, []);

  const getAdminDashboardPath = () => {
    switch (user?.role) {
      case 'main_admin': return '/admin/main/dashboard';
      case 'admin': return '/admin/main/dashboard';
      case 'invoicer': return '/admin/main/invoicing';
      case 'restaurant_owner': return '/admin/restaurant/dashboard';
      case 'store_owner': return '/admin/store/dashboard';
      case 'delivery_driver': return '/admin/driver/dashboard';
      default: return '/admin/login';
    }
  };

  const handleLogout = () => {
    // Clear cart and delivery address
    clearCart();
    setCurrentAddress(null);
    // Logout and navigate
    logout(() => {
      // Force page refresh to ensure all state is cleared
      window.location.href = '/';
    });
  };

  const handleLanguageChange = async (e) => {
    const newLanguage = e.target?.value || e;
    setCurrentLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('i18nextLng', newLanguage);
    if (user) {
      try {
        await axios.put(`${API_URL}/users/settings`, { language: newLanguage });
        updateUser({ ...user, settings: { ...user.settings, language: newLanguage } });
      } catch (error) {
        console.error('Error updating language:', error);
      }
    }
  };

  const handleThemeChange = async (value) => {
    if (value !== 'light' && value !== 'dark' && value !== 'device') return;
    setThemePreference(value);
    if (user) {
      try {
        const newSettings = { ...(user.settings || {}), themePreference: value };
        const response = await axios.put(`${API_URL}/users/settings`, newSettings);
        if (response.data?.settings) {
          updateUser({ ...user, settings: response.data.settings });
        }
      } catch (error) {
        console.error('Error updating theme:', error);
      }
    }
  };

  return (
    <nav className={`navbar ${isNavbarVisible ? 'visible' : 'hidden'}`}>
      <div className="navbar-container">
        {/* Hamburger - left on all resolutions */}
        <button
          className="navbar-hamburger"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Toggle menu"
        >
          <i className="fas fa-bars"></i>
        </button>

        {/* Center: logo + address */}
        <div className="navbar-center">
          <Link to="/" className="navbar-logo">
            <span className="navbar-logo-text">PUSSIKUSKIT</span>
            <img
              src={`${process.env.PUBLIC_URL || ''}/images/logo.webp`}
              alt="Pussikuskit"
              className="logo-image"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </Link>
          <div className="navbar-address-desktop">
            <AddressSelector />
          </div>
        </div>

        <div className="navbar-address-mobile">
          <AddressSelector />
        </div>
      </div>

      {/* Slide-out menu (all resolutions) */}
      <div className={`mobile-menu-overlay ${showMobileMenu ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
        <div className={`mobile-menu navbar-mobile-menu ${showMobileMenu ? 'active' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <Link to="/" className="mobile-menu-logo" onClick={() => setShowMobileMenu(false)}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/images/logo.webp`}
                alt="Pussikuskit"
                className="logo-image"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="mobile-menu-logo-text">PUSSIKUSKIT</span>
            </Link>
            <button
              className="mobile-menu-close"
              onClick={() => setShowMobileMenu(false)}
              aria-label="Close menu"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="mobile-menu-items">
            <Link to="/" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
              <i className="fas fa-home"></i>
              <span>{t('nav.home')}</span>
            </Link>
            <Link to="/restaurants" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
              <i className="fas fa-utensils"></i>
              <span>{t('nav.restaurants')}</span>
            </Link>
            {hasStores && (
              <Link to="/stores" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                <i className="fas fa-store"></i>
                <span>{t('nav.stores')}</span>
              </Link>
            )}
            <Link to="/cart" className="mobile-menu-item cart-item-mobile" onClick={() => setShowMobileMenu(false)}>
              <div className="cart-icon-wrapper">
                <i className="fas fa-shopping-cart"></i>
                {cartItemCount > 0 && (
                  <span className="cart-badge-mobile">{cartItemCount}</span>
                )}
              </div>
              <span>{t('nav.cart')}</span>
            </Link>
            {user && (
              <>
                <div className="mobile-menu-divider" />
                <Link to="/account" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <i className="fas fa-user"></i>
                  <span>{t('nav.account')}</span>
                </Link>
                <Link to="/wallet" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <i className="fas fa-wallet"></i>
                  <span>{t('nav.wallet')}</span>
                </Link>
                <Link to="/settings" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <i className="fas fa-cog"></i>
                  <span>{t('nav.settings')}</span>
                </Link>
                <Link to="/orders" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <i className="fas fa-receipt"></i>
                  <span>{t('nav.orders')}</span>
                </Link>
              </>
            )}
            <Link to="/help" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
              <i className="fas fa-question-circle"></i>
              <span>{t('nav.help')}</span>
            </Link>
            <Link to="/information" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
              <i className="fas fa-info-circle"></i>
              <span>{t('nav.information')}</span>
            </Link>
            {user && (
                <>
                {['main_admin', 'admin', 'invoicer', 'delivery_driver', 'restaurant_owner', 'store_owner'].includes(user?.role) && (
                  <Link to={getAdminDashboardPath()} className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <i className="fas fa-tools"></i>
                    <span>Admin Menu</span>
                  </Link>
                )}
              </>
            )}
            <div className="mobile-menu-divider" />
            <div className="mobile-menu-section-label">{t('nav.theme')}</div>
            <div className="mobile-menu-theme-row">
              <button
                type="button"
                className={`mobile-menu-theme-btn ${themePreference === 'light' ? 'active' : ''}`}
                onClick={() => { handleThemeChange('light'); setShowMobileMenu(false); }}
              >
                {t('settings.themeLight') || 'Light'}
              </button>
              <button
                type="button"
                className={`mobile-menu-theme-btn ${themePreference === 'dark' ? 'active' : ''}`}
                onClick={() => { handleThemeChange('dark'); setShowMobileMenu(false); }}
              >
                {t('settings.themeDark') || 'Dark'}
              </button>
              <button
                type="button"
                className={`mobile-menu-theme-btn ${themePreference === 'device' ? 'active' : ''}`}
                onClick={() => { handleThemeChange('device'); setShowMobileMenu(false); }}
              >
                {t('settings.themeFollowDevice') || 'Follow device'}
              </button>
            </div>
            <div className="mobile-menu-section-label">{t('nav.language')}</div>
            <div className="mobile-menu-lang-row">
              <button
                type="button"
                className={`mobile-menu-lang-btn ${currentLanguage === 'fi' ? 'active' : ''}`}
                onClick={() => { handleLanguageChange('fi'); setShowMobileMenu(false); }}
              >
                Suomi
              </button>
              <button
                type="button"
                className={`mobile-menu-lang-btn ${currentLanguage === 'en' ? 'active' : ''}`}
                onClick={() => { handleLanguageChange('en'); setShowMobileMenu(false); }}
              >
                English
              </button>
            </div>
            {user && (
              <>
                <div className="mobile-menu-divider" />
                <button
                  type="button"
                  className="mobile-menu-item mobile-menu-item-btn"
                  onClick={() => { setShowMobileMenu(false); handleLogout(); }}
                >
                  <i className="fas fa-sign-out-alt"></i>
                  <span>{t('nav.logout')}</span>
                </button>
              </>
            )}
            {!user && (
              <Link to="/login" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                <i className="fas fa-lock"></i>
                <span>{t('nav.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
